<template>
  <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
    <BaseForm
      ref="form"
      :action="
        () => {
          return handleSubmit(submit);
        }
      "
      v-slot="formProps"
    >
      <h5 class="text-sm font-medium tracking-wide text-gray-700 mb-2">
        Assigned to
        <span v-if="formProps.isResolved" key="resolved" class="text-green-600">Updated</span
        ><span v-if="formProps.isRejected" key="rejected" class="text-red-600">Failed</span>
      </h5>

      <VMultiselect
        v-model="userAssigned"
        :options="options"
        :allow-empty="!isPublished"
        group-values="members"
        group-label="name"
        track-by="id"
        label="fullName"
        :disabled="!editable || formProps.isPending"
        class="w-80"
      />
    </BaseForm>
  </ValidationObserver>
</template>

<script>
export default {
  name: 'TaskAssignee',

  props: {
    editable: Boolean,
    task: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      formData: {
        accountAssignedId: this.task.accountAssignedId,
        userAssignedId: this.task.userAssignedId,
      },
    };
  },

  computed: {
    account() {
      return this.$store.state.entities.accounts.byId[this.task.accountUuid];
    },
    accountMembers() {
      return this.$store.getters['ui/accountMembers/membersByAccount'](this.task.accountUuid);
    },
    accountProviders() {
      return this.$store.getters['ui/accountProviders/providersByAccount'](this.task.accountUuid);
    },
    options() {
      return [
        {
          id: this.account.id,
          name: this.account.name,
          members: this.transformAccountMembers(this.account, this.accountMembers),
        },
        ...this.accountProviders.map((accountProvider) => ({
          id: accountProvider.id,
          name: accountProvider.name,
          members: this.transformAccountMembers(
            accountProvider,
            accountProvider.members
              .filter(
                (memberId) =>
                  this.accountMembers.findIndex(
                    (accountMember) => accountMember.id === memberId
                  ) === -1
              )
              .map((memberId) => this.$store.state.entities.users.byId[memberId])
          ),
        })),
      ];
    },
    members() {
      return this.options.reduce((carry, option) => [...carry, ...option.members], []);
    },
    userAssigned: {
      get() {
        if (!this.formData?.userAssignedId || !this.formData?.accountAssignedId) {
          return null;
        }

        return this.members.find(
          (member) =>
            member.id === this.formData.userAssignedId &&
            member.accountId === this.formData.accountAssignedId
        );
      },
      set(member) {
        this.formData.userAssignedId = member !== null ? member.id : null;
        this.formData.accountAssignedId = member !== null ? member.accountId : null;
      },
    },
    isPublished() {
      return typeof this.task.publishedAt === 'string';
    },
  },

  watch: {
    'task.userAssignedId': {
      handler(newUserAssignedId) {
        this.formData.userAssignedId = newUserAssignedId;
      },
    },
    'task.accountAssignedId': {
      handler(newAccountAssignedId) {
        this.formData.accountAssignedId = newAccountAssignedId;
      },
    },
    formData: {
      deep: true,
      handler() {
        this.$refs.form.handleSubmit();
      },
    },
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      try {
        await this.$store.dispatch({
          type: 'ui/accountMembers/load',
          accountId: this.task.accountUuid,
        });
        await this.$store.dispatch({
          type: 'ui/accountProviders/load',
          accountId: this.task.accountUuid,
          params: {
            include: 'members',
          },
        });
      } catch (error) {
        //
      }
    },
    transformAccountMembers(account, members) {
      return members.map((member) => ({
        id: member.id,
        fullName: member.fullName,
        accountId: account.id,
      }));
    },
    async submit() {
      try {
        await this.$store.dispatch({
          type: 'ui/projectTasks/update',
          projectId: this.task.projectId,
          taskId: this.task.uuid,
          formData: this.formData,
        });

        this.$emit('task:updated');
      } catch (error) {
        // should handle 244 errors
      }
    },
  },
};
</script>
